import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen, Search, Home, UserPlus, BadgeCheck, LayoutDashboard, Briefcase,
  Handshake, Users, Crown, MessageSquare, Share2, UserCircle2, Bell, Smartphone,
  Shield, ChevronRight, Sparkles, AlertTriangle, CheckCircle2, ArrowRight,
  Gavel, Wallet, FileText, Star, ScrollText, Clock, MapPin,
} from "lucide-react";

const SECTIONS = [
  { id: "intro", icon: BookOpen, title: "Antes de empezar", short: "Qué es Colega Link" },
  { id: "registro", icon: UserPlus, title: "1. Registro", short: "Crea tu cuenta" },
  { id: "verificacion", icon: BadgeCheck, title: "2. Verificación", short: "Sube tus credenciales" },
  { id: "dashboard", icon: LayoutDashboard, title: "3. Panel principal", short: "Tu centro de control" },
  { id: "delegaciones", icon: Briefcase, title: "4. Delegaciones", short: "Audiencias y trámites" },
  { id: "delegacion-flujo", icon: Gavel, title: "5. Flujo de una delegación", short: "Paso a paso" },
  { id: "mediaciones", icon: Handshake, title: "6. Mediaciones", short: "Para mediadores familiares" },
  { id: "mediadores", icon: Users, title: "7. Directorio Mediadores", short: "Buscar profesionales" },
  { id: "membresia", icon: Crown, title: "8. Membresía y Pionero", short: "Planes y beneficios" },
  { id: "foro", icon: MessageSquare, title: "9. Foro de consultas", short: "Q&A entre colegas" },
  { id: "invitar", icon: Share2, title: "10. Invitar colegas", short: "Plantillas WhatsApp" },
  { id: "perfil", icon: UserCircle2, title: "11. Perfil", short: "Tus datos y foto" },
  { id: "notificaciones", icon: Bell, title: "12. Notificaciones", short: "Bell + Push WhatsApp-style" },
  { id: "pwa", icon: Smartphone, title: "13. Instalar la app", short: "Como app nativa" },
  { id: "reglas", icon: Shield, title: "14. Reglas de oro", short: "Lo que NO debes hacer" },
  { id: "soporte", icon: Sparkles, title: "15. Soporte", short: "Cómo pedir ayuda" },
];

export default function Guia() {
  const [active, setActive] = useState("intro");
  const [query, setQuery] = useState("");

  const filteredSections = useMemo(() => {
    if (!query.trim()) return SECTIONS;
    const q = query.toLowerCase();
    return SECTIONS.filter((s) => s.title.toLowerCase().includes(q) || s.short.toLowerCase().includes(q));
  }, [query]);

  const Active = SECTIONS.find((s) => s.id === active) || SECTIONS[0];

  return (
    <div className="container-app py-10" data-testid="guia-page">
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <div className="label-eyebrow">Guía de inducción</div>
            <h1 className="serif text-3xl mt-2 leading-tight">Cómo usar <span className="brand-gradient-text">Colega Link</span></h1>
            <p className="mt-3 text-xs text-muted-foreground">15 minutos · todo lo que necesitas saber.</p>

            <div className="mt-6 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar sección…"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-white/10 bg-white/5 text-sm outline-none focus:border-[hsl(var(--accent))]"
                data-testid="guia-search"
              />
            </div>

            <nav className="mt-5 space-y-1" data-testid="guia-nav">
              {filteredSections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setActive(s.id);
                    document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                    active === s.id ? "bg-white/10 text-foreground" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                  }`}
                  data-testid={`guia-nav-${s.id}`}
                >
                  <s.icon size={14} className="shrink-0" />
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="lg:col-span-9 space-y-16 pb-24">
          <SectionIntro />
          <SectionRegistro />
          <SectionVerificacion />
          <SectionDashboard />
          <SectionDelegaciones />
          <SectionDelegacionFlujo />
          <SectionMediaciones />
          <SectionMediadores />
          <SectionMembresia />
          <SectionForo />
          <SectionInvitar />
          <SectionPerfil />
          <SectionNotificaciones />
          <SectionPWA />
          <SectionReglas />
          <SectionSoporte />
        </main>
      </div>
    </div>
  );
}

// ============================================================================
// Reusable building blocks
// ============================================================================

function Section({ id, icon: Icon, title, eyebrow, children }) {
  return (
    <section id={`section-${id}`} className="scroll-mt-24" data-testid={`guia-section-${id}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent))]/15 flex items-center justify-center ring-1 ring-[hsl(var(--accent))]/30">
          <Icon size={18} className="text-[hsl(var(--accent))]" />
        </div>
        <div className="label-eyebrow">{eyebrow}</div>
      </div>
      <h2 className="serif text-3xl md:text-4xl leading-tight">{title}</h2>
      <div className="mt-6 space-y-4">{children}</div>
    </section>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="panel p-5 flex gap-4">
      <div className="shrink-0 w-9 h-9 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] font-bold flex items-center justify-center text-sm">{n}</div>
      <div>
        <div className="font-medium">{title}</div>
        <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Callout({ type = "info", title, children }) {
  const styles = {
    info: { ring: "ring-cyan-400/30", bg: "bg-cyan-400/5", icon: "text-cyan-400", Icon: Sparkles },
    warn: { ring: "ring-amber-400/40", bg: "bg-amber-400/5", icon: "text-amber-400", Icon: AlertTriangle },
    success: { ring: "ring-emerald-400/30", bg: "bg-emerald-400/5", icon: "text-emerald-400", Icon: CheckCircle2 },
    danger: { ring: "ring-red-400/30", bg: "bg-red-400/5", icon: "text-red-400", Icon: AlertTriangle },
  }[type];
  const I = styles.Icon;
  return (
    <div className={`panel p-5 ring-1 ${styles.ring} ${styles.bg}`}>
      <div className="flex items-start gap-3">
        <I size={18} className={`${styles.icon} shrink-0 mt-0.5`} />
        <div>
          {title && <div className="font-medium">{title}</div>}
          <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="text-sm">
      <span className="text-muted-foreground">{label}:</span> <span className="text-foreground">{value}</span>
    </div>
  );
}

function Pill({ children, color = "neutral" }) {
  const c = {
    neutral: "bg-white/5 text-muted-foreground border-white/10",
    amber: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    emerald: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
    cyan: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30",
    red: "bg-red-400/15 text-red-300 border-red-400/30",
  }[color];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wider border ${c}`}>{children}</span>;
}

// ============================================================================
// Section contents
// ============================================================================

function SectionIntro() {
  return (
    <Section id="intro" icon={BookOpen} title="¿Qué es Colega Link?" eyebrow="Bienvenida">
      <p className="text-base text-muted-foreground leading-relaxed">
        Colega Link es la <strong className="text-foreground">primera red de abogados y mediadores verificados de Chile</strong> diseñada para
        resolver dos problemas que todos hemos vivido: encontrar un colega de confianza para cubrir una audiencia
        urgente o gestionar mediaciones familiares sin perder horas en llamadas y WhatsApps.
      </p>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="panel p-5">
          <Briefcase size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 serif text-lg">Delegaciones</div>
          <p className="mt-1 text-xs text-muted-foreground">Audiencias y trámites cubiertos por colegas en toda Chile, con Pago Protegido Colega Link.</p>
        </div>
        <div className="panel p-5">
          <Handshake size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 serif text-lg">Mediaciones</div>
          <p className="mt-1 text-xs text-muted-foreground">Gestión completa de mediaciones familiares con mediadores del Registro del Ministerio.</p>
        </div>
        <div className="panel p-5">
          <Shield size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 serif text-lg">Verificación</div>
          <p className="mt-1 text-xs text-muted-foreground">Validamos carnet, título y certificado de la Corte Suprema antes de habilitarte.</p>
        </div>
      </div>

      <Callout type="info" title="¿Para quién es?">
        <ul className="space-y-1 mt-1">
          <li>• <strong>Abogados</strong> con título validado por el Estado de Chile (puedes ser delegante o delegado).</li>
          <li>• <strong>Mediadores</strong> inscritos en el Registro de Mediadores del Ministerio de Justicia.</li>
        </ul>
      </Callout>

      <Callout type="warn" title="¿Para quién NO es?">
        Estudiantes de Derecho, egresados sin título, gestores judiciales, abogados de otros países sin convalidación.
        El sistema bloquea automáticamente cuentas sin verificación válida.
      </Callout>
    </Section>
  );
}

function SectionRegistro() {
  return (
    <Section id="registro" icon={UserPlus} title="Crea tu cuenta en 90 segundos" eyebrow="Paso 1">
      <p className="text-sm text-muted-foreground">
        Ve a <Link to="/register" className="underline hover:text-foreground">colegalink.cl/register</Link> y completa el formulario.
        Toda la información se valida antes de habilitarte.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <Step n="1" title="Datos personales">
          Nombre completo, email (será tu usuario), contraseña segura y RUT chileno.
        </Step>
        <Step n="2" title="Rol profesional">
          Elige entre <Pill color="cyan">Abogado/a</Pill> o <Pill color="emerald">Mediador/a familiar</Pill>.
        </Step>
        <Step n="3" title="Ubicación">
          Región y comuna donde ejerces (define dónde aparecerás en búsquedas y matching).
        </Step>
        <Step n="4" title="Especialidades">
          Civil, Familia, Laboral, Penal, etc. Máximo 5 — sé honesto: tu reputación depende de esto.
        </Step>
        <Step n="5" title="Número de registro">
          Tu N° de inscripción en la Corte Suprema (abogados) o N° del Registro de Mediadores (mediadores).
        </Step>
        <Step n="6" title="Código de invitación (opcional)">
          Si te invitó un colega Fundador, ingresa su código <Pill color="amber">AMIGO-N1-XXXX</Pill> para obtener
          50% de descuento en tu primera anualidad.
        </Step>
      </div>

      <Callout type="success" title="Tip">
        Bio profesional corta (2-3 líneas): años de experiencia, áreas fuertes y tribunales donde más actúas.
        Es lo primero que ven los colegas que evalúan postularte.
      </Callout>
    </Section>
  );
}

function SectionVerificacion() {
  return (
    <Section id="verificacion" icon={BadgeCheck} title="Verificación de credenciales" eyebrow="Paso 2 · crítico">
      <p className="text-sm text-muted-foreground">
        Después de registrarte recibes un email de bienvenida. Para poder <strong className="text-foreground">postular a delegaciones</strong>,
        <strong className="text-foreground"> publicar mediaciones</strong> o <strong className="text-foreground">aceptar pagos</strong>,
        necesitas verificar tres documentos:
      </p>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="panel p-5">
          <FileText size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 font-medium">Cédula de identidad</div>
          <p className="mt-1 text-xs text-muted-foreground">Foto del frente, legible, vigente.</p>
        </div>
        <div className="panel p-5">
          <ScrollText size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 font-medium">Título profesional</div>
          <p className="mt-1 text-xs text-muted-foreground">PDF o foto del diploma original.</p>
        </div>
        <div className="panel p-5">
          <Shield size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 font-medium">Certificado Corte Suprema</div>
          <p className="mt-1 text-xs text-muted-foreground">Solo abogados. Descárgalo en <a href="https://www.pjud.cl" target="_blank" rel="noreferrer" className="underline">pjud.cl</a>.</p>
        </div>
      </div>

      <Callout type="info" title="¿Cómo subir tus documentos?">
        Una vez logueado, ve a <Pill>Perfil</Pill> en la barra superior. Arriba verás un panel con tres cards (Cédula,
        Título, Certificado CS). Pincha "Subir" en cada uno y selecciona el archivo (PDF, JPG o PNG, máx 5 MB).
      </Callout>

      <Callout type="warn" title="Tiempos de verificación">
        Embajadores Pioneros: <strong className="text-foreground">24 horas hábiles</strong> (verificación prioritaria).
        Usuarios estándar (post-lanzamiento): hasta <strong className="text-foreground">48 horas hábiles</strong>.
        Recibirás un email + notificación in-app cuando estés verificado.
      </Callout>
    </Section>
  );
}

function SectionDashboard() {
  return (
    <Section id="dashboard" icon={LayoutDashboard} title="Tu panel principal" eyebrow="Paso 3">
      <p className="text-sm text-muted-foreground">
        El Panel <Link to="/app" className="underline hover:text-foreground">colegalink.cl/app</Link> es tu centro de operaciones.
        Lo que ves depende de tu rol:
      </p>

      <div className="panel p-5">
        <div className="font-medium flex items-center gap-2"><Briefcase size={16} className="text-[hsl(var(--accent))]" /> Si eres Abogado</div>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          <Bullet><strong className="text-foreground">Trabajos activos asignados a ti</strong>: delegaciones donde fuiste elegido y están en curso. Click para entrar al detalle.</Bullet>
          <Bullet><strong className="text-foreground">Tus publicaciones</strong>: delegaciones que tú publicaste, con estado y postulaciones recibidas.</Bullet>
          <Bullet><strong className="text-foreground">Directorio</strong>: delegaciones abiertas en toda Chile a las que puedes postular.</Bullet>
          <Bullet><strong className="text-foreground">Mediaciones activas</strong>: si tienes mediaciones en curso (como representante de una de las partes).</Bullet>
        </ul>
      </div>

      <div className="panel p-5">
        <div className="font-medium flex items-center gap-2"><Handshake size={16} className="text-[hsl(var(--accent))]" /> Si eres Mediador</div>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          <Bullet><strong className="text-foreground">Mediaciones activas</strong>: las que tienes en curso. Ranking por estado (pendiente / en proceso / pago liberado).</Bullet>
          <Bullet><strong className="text-foreground">Solicitudes nuevas</strong>: invitaciones que abogados te enviaron para mediar.</Bullet>
        </ul>
      </div>

      <Callout type="success" title="Tip de uso">
        Revisa el Panel cada mañana. Las delegaciones del Directorio se mueven rápido — la primera postulación
        suele ganar el cupo si es competitiva en precio.
      </Callout>
    </Section>
  );
}

function SectionDelegaciones() {
  return (
    <Section id="delegaciones" icon={Briefcase} title="Delegaciones" eyebrow="Paso 4">
      <p className="text-sm text-muted-foreground">
        Una <strong className="text-foreground">delegación</strong> es cuando un abogado le pide a otro colega que
        cubra una actuación judicial: audiencia, comparendo, alegato, notificación, etc.
      </p>

      <div className="panel p-5">
        <div className="font-medium">Listado en <Link to="/app/delegaciones" className="underline hover:text-foreground">/app/delegaciones</Link></div>
        <p className="mt-2 text-sm text-muted-foreground">Filtros disponibles:</p>
        <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
          <Bullet><Pill color="emerald">Abiertas</Pill> — disponibles para postular.</Bullet>
          <Bullet><Pill color="cyan">Asignadas</Pill> — ya tienen un colega elegido.</Bullet>
          <Bullet><Pill color="amber">En revisión</Pill> — el comprobante fue subido, esperando aprobación.</Bullet>
          <Bullet><Pill color="emerald">Completadas</Pill> — pago liberado al delegado.</Bullet>
          <Bullet><Pill color="red">Canceladas</Pill> — el publicador desistió.</Bullet>
        </ul>
      </div>

      <div className="panel p-5">
        <div className="font-medium flex items-center gap-2"><Plus size={16} /> Publicar una delegación</div>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
          <li><strong className="text-foreground">Tribunal</strong>: Corte de Apelaciones de Santiago, JG de Concepción, etc.</li>
          <li><strong className="text-foreground">Tipo de actuación</strong>: audiencia de juicio, comparendo, alegato, notificación.</li>
          <li><strong className="text-foreground">Fecha y hora</strong>: cuándo debe ocurrir.</li>
          <li><strong className="text-foreground">Honorario propuesto</strong> en CLP. El sistema sugiere rangos típicos por tipo + región.</li>
          <li><strong className="text-foreground">Observaciones</strong>: contexto del caso, ROL, partes involucradas. <em>No expongas datos sensibles aquí.</em></li>
        </ol>
        <p className="mt-3 text-xs text-muted-foreground">Una vez publicada queda visible en el Directorio para postulaciones.</p>
      </div>
    </Section>
  );
}

function Plus({ size = 16 }) {
  return <span className="w-4 h-4 inline-flex items-center justify-center" style={{ fontSize: size }}>+</span>;
}

function SectionDelegacionFlujo() {
  return (
    <Section id="delegacion-flujo" icon={Gavel} title="Flujo paso a paso de una delegación" eyebrow="Paso 5 · el más importante">
      <Callout type="info">
        Este es el corazón de Colega Link. Léelo con calma porque define cómo se mueve el dinero y cómo se protegen ambos colegas.
      </Callout>

      <div className="space-y-3">
        <Step n="1" title="Publicas la delegación">
          Defines tribunal, fecha, hora, honorario y observaciones. La publicación es gratis.
        </Step>
        <Step n="2" title="Colegas postulan (bids)">
          Cualquier abogado verificado puede postular. Puede aceptar tu precio o hacer una <strong className="text-foreground">contraoferta</strong>.
          Verás un listado ordenado por precio + reputación del postulante.
        </Step>
        <Step n="3" title="Eliges al colega y activas el Pago Protegido">
          Click "Asignar" sobre tu postulante elegido → te lleva al checkout Pago Protegido.
          Pagas el honorario + cargo Colega Link. <strong className="text-foreground">El dinero queda retenido</strong>, no se libera al delegado todavía.
        </Step>
        <Step n="4" title="Subes los antecedentes">
          Carpeta del caso, escritos, exhortos, lo que necesite tu colega. Solo el delegado asignado los puede descargar.
        </Step>
        <Step n="5" title="Generas la Minuta de Delega Poder">
          Click <Pill color="amber">Descargar Minuta</Pill> — el sistema te genera un HTML auto-rellenado con tus datos y los del delegado para firmar
          ante notario o con FEA.
        </Step>
        <Step n="6" title="El delegado ejecuta y sube el comprobante">
          Acta de audiencia, acuse del tribunal, certificado de comparendo. Lo que corresponda.
        </Step>
        <Step n="7" title="Liberas el pago">
          Revisas el comprobante. Si todo OK, click <Pill color="emerald">Aprobar y liberar pago</Pill> → el dinero retenido se transfiere al delegado
          (menos la comisión Colega Link). Si hay problema, abres una <Pill color="red">disputa</Pill> y el equipo media.
        </Step>
        <Step n="8" title="Califican mutuamente">
          Tú calificas al delegado (puntualidad, calidad, comunicación) y viceversa. Esto construye la reputación de ambos.
        </Step>
      </div>

      <Callout type="warn" title="Tiempos de liberación">
        Tienes <strong className="text-foreground">5 días corridos</strong> para revisar el comprobante. Si no apruebas ni reclamas,
        el sistema libera automáticamente el pago a favor del delegado.
      </Callout>

      <Callout type="danger" title="Anti-desintermediación (CRÍTICO)">
        <strong>Nunca</strong> intercambies WhatsApp, teléfono o email con el otro colega antes de la asignación.
        El sistema bloquea esos patrones automáticamente en el chat. Operar fuera de Colega Link después de hacer match
        viola los Términos y puede resultar en suspensión permanente + cobro de comisión.
      </Callout>
    </Section>
  );
}

function SectionMediaciones() {
  return (
    <Section id="mediaciones" icon={Handshake} title="Mediaciones familiares" eyebrow="Paso 6">
      <p className="text-sm text-muted-foreground">
        Las mediaciones funcionan similar a las delegaciones, pero con 3 partes: el <strong className="text-foreground">abogado solicitante</strong>,
        el <strong className="text-foreground">mediador</strong> y la <strong className="text-foreground">contraparte</strong>.
      </p>

      <div className="panel p-5">
        <div className="font-medium">Flujo simplificado</div>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
          <li>Un abogado publica una solicitud de mediación (caso de pensión alimentos, régimen comunicacional, divorcio, etc.).</li>
          <li>Mediadores verificados postulan con honorario y disponibilidad.</li>
          <li>El abogado elige al mediador y activa el Pago Protegido.</li>
          <li>El mediador convoca a la contraparte y conduce las sesiones.</li>
          <li>Al cerrar (con acuerdo o sin), se sube el acta firmada.</li>
          <li>Se libera el pago al mediador y se califican entre ambos.</li>
        </ol>
      </div>

      <Callout type="info" title="Diferencia con delegaciones">
        Las mediaciones permiten <strong className="text-foreground">múltiples sesiones</strong> y un timeline de estados más largo
        (convocatoria → primera sesión → acuerdos parciales → cierre). El detalle de mediación tiene una sección de "Bitácora"
        donde mediador y partes registran lo conversado en cada sesión.
      </Callout>
    </Section>
  );
}

function SectionMediadores() {
  return (
    <Section id="mediadores" icon={Users} title="Directorio de mediadores" eyebrow="Paso 7">
      <p className="text-sm text-muted-foreground">
        Si eres abogado y necesitas un mediador específico, no es obligatorio publicar una solicitud abierta:
        puedes buscar directamente en el directorio <Link to="/app/mediadores" className="underline hover:text-foreground">/app/mediadores</Link>.
      </p>

      <div className="panel p-5">
        <div className="font-medium">Filtros disponibles</div>
        <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
          <Bullet><MapPin size={11} className="inline mr-1" /> <strong className="text-foreground">Región / comuna</strong>: encuentra mediadores cerca de tu caso.</Bullet>
          <Bullet><Star size={11} className="inline mr-1" /> <strong className="text-foreground">Especialidad</strong>: pensión alimentos, régimen comunicacional, divorcios, etc.</Bullet>
          <Bullet><Search size={11} className="inline mr-1" /> <strong className="text-foreground">Búsqueda libre</strong>: por nombre o N° de registro.</Bullet>
        </ul>
      </div>

      <Callout type="success" title="Tip">
        El perfil de cada mediador muestra <strong className="text-foreground">rating promedio</strong>, <strong>casos completados</strong>,
        bio profesional y disponibilidad. Desde ahí puedes iniciar una mediación directa sin publicar al Directorio.
      </Callout>
    </Section>
  );
}

function SectionMembresia() {
  const { user } = useAuth();
  const isMediator = user?.role === "mediator";
  if (isMediator) {
    return (
      <Section id="membresia" icon={Crown} title="Membresía Mediador" eyebrow="Paso 8">
        <p className="text-sm text-muted-foreground">
          Como mediador/a familiar, en Colega Link operas bajo la <strong className="text-foreground">Ley 21.226 de
          Mediación Familiar Privada</strong>: cobras tu honorario libremente dentro del tope legal ($130.602 al solicitante),
          y ColegaLink cobra un <strong className="text-foreground">cargo fijo de 0.35 UF al iniciar</strong> más un
          <strong className="text-foreground"> 8% sólo sobre el precio adicional si se firma acuerdo total</strong>.
          No hay membresía anual ni comisión sobre tu honorario base.
        </p>
        <Callout type="info" title="¿Cómo activo mi perfil de mediador?">
          Completa tus datos de perfil (RUT, comunas, agenda semanal y tarifario) desde
          <Link to="/app/perfil" className="underline hover:text-foreground"> Perfil</Link> — no necesitas suscripción.
        </Callout>
      </Section>
    );
  }
  return (
    <Section id="membresia" icon={Crown} title="Membresía y Pionero Fundador" eyebrow="Paso 8">
      <p className="text-sm text-muted-foreground">
        Colega Link tiene 4 planes. Hoy el más relevante es el programa <strong className="text-amber-300">Pionero</strong>:
        50 cupos exclusivos con <strong className="text-foreground">reserva de $15.000 CLP</strong> que se abona al plan
        anual Pionero Fundador (7.0 UF) si completas el reto de 15 referidos validados.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="panel p-5 ring-1 ring-amber-400/40 bg-amber-400/5">
          <Crown size={18} className="text-amber-400" />
          <div className="mt-2 font-medium text-amber-300">Reserva Pionero</div>
          <div className="text-2xl serif mt-1">$15.000 CLP</div>
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
            <Bullet>Cupo Fundador vitalicio</Bullet>
            <Bullet>Badge &ldquo;Pionero N°XX&rdquo;</Bullet>
            <Bullet>0% comisión en las primeras 10 delegaciones</Bullet>
            <Bullet>30 consultas/mes Colega IA</Bullet>
            <Bullet>Abono íntegro al plan anual Fundador (7 UF)</Bullet>
          </ul>
          <Link to="/embajador-pionero" className="mt-4 btn-ghost text-xs inline-flex items-center gap-1" data-testid="guia-pionero-link">
            Ver landing Pionero <ChevronRight size={12} />
          </Link>
        </div>

        <div className="panel p-5">
          <Sparkles size={18} className="text-violet-400" />
          <div className="mt-2 font-medium">Planes regulares</div>
          <ul className="mt-3 space-y-1.5 text-xs text-muted-foreground">
            <li><Pill>Free</Pill> Postular a delegaciones · 12% comisión</li>
            <li><Pill color="cyan">Premium</Pill> 0.85 UF/mes o 8.5 UF/año · 7% comisión (mín 0.2 UF)</li>
            <li><Pill color="amber">Pionero Fundador</Pill> 0.75 UF/mes o 7.0 UF/año · 0% vitalicio</li>
          </ul>
          <p className="mt-2 text-[10px] text-muted-foreground">Valor CLP se calcula al día según la UF (mindicador.cl).</p>
          <Link to="/planes" className="mt-4 btn-ghost text-xs inline-flex items-center gap-1">
            Ver precios completos <ChevronRight size={12} />
          </Link>
        </div>
      </div>

      <Callout type="info" title="¿Cómo activo mi plan?">
        Loguéate y ve a <Link to="/app/membresia" className="underline hover:text-foreground">Membresía</Link> en el menú.
        <MediatorAware fallback={<>Si quieres reservar tu cupo Pionero, ve directo a <Link to="/app/membresia/reservar" className="underline hover:text-foreground"> Reservar cupo</Link>.</>}>
          {null}
        </MediatorAware>
      </Callout>
    </Section>
  );
}

function SectionForo() {
  return (
    <Section id="foro" icon={MessageSquare} title="Foro de consultas (Q&A)" eyebrow="Paso 9">
      <p className="text-sm text-muted-foreground">
        Plataforma estilo StackOverflow para colegas. Publica dudas, vota respuestas, marca la solución correcta.
        Ideal para casos de jurisprudencia, tácticas procesales, dudas administrativas.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="panel p-5">
          <div className="font-medium">Cómo funciona</div>
          <ol className="mt-3 space-y-1.5 text-sm text-muted-foreground list-decimal list-inside">
            <li>Ve a <Link to="/app/foro" className="underline hover:text-foreground">/app/foro</Link>.</li>
            <li>Click "Nueva consulta" → título corto, descripción y tags (civil, familia, laboral, etc.).</li>
            <li>Los colegas responden y votan ↑ las mejores respuestas.</li>
            <li>El autor marca <Pill color="emerald">Respuesta aceptada</Pill> a la mejor.</li>
          </ol>
        </div>
        <div className="panel p-5 ring-1 ring-amber-400/40 bg-amber-400/5">
          <AlertTriangle size={16} className="text-amber-400" />
          <div className="mt-2 font-medium">Anti-desintermediación</div>
          <p className="mt-2 text-xs text-muted-foreground">
            El foro <strong className="text-foreground">oculta automáticamente</strong> números de teléfono, emails y enlaces externos
            en preguntas y respuestas. Esto protege tu cartera de clientes de "pesca" externa.
          </p>
        </div>
      </div>
    </Section>
  );
}

function SectionInvitar() {
  return (
    <Section id="invitar" icon={Share2} title="Invita colegas por WhatsApp" eyebrow="Paso 10">
      <p className="text-sm text-muted-foreground">
        Cada usuario tiene un <strong className="text-foreground">código de referido único</strong>. Cuando un colega se
        registra con tu código, ambos ganan: ellos un 50% de descuento en su anualidad, tú avanzas en la escala
        Pionero de comisión 0%.
      </p>

      <div className="panel p-5">
        <div className="font-medium">Cómo invitar en 1 minuto</div>
        <ol className="mt-3 space-y-2 text-sm text-muted-foreground list-decimal list-inside">
          <li>Ve a <Link to="/app/invitar" className="underline hover:text-foreground">/app/invitar</Link>.</li>
          <li>Tu código de referido aparece arriba (formato <Pill color="amber">AMIGO-N1-XXXX</Pill>).</li>
          <li>Escribe el nombre del contacto en el input para personalizar todas las plantillas.</li>
          <li>Elige plantilla según tu cercanía: Principal (estándar), Corto (segundo contacto), Cercano (amigo directo), Inscritos (ya registrado pero no completó).</li>
          <li>Click <Pill color="emerald">Compartir WhatsApp</Pill> → se abre WhatsApp con el mensaje listo.</li>
          <li>Elige el contacto en WhatsApp y envía.</li>
        </ol>
      </div>

      <Callout type="warn" title="Reglas de etiqueta">
        Máx 20 mensajes/hora para evitar marca de spam. Solo a contactos que conoces. Si te dicen "no", déjalo pasar:
        no insistas más de 2 veces.
      </Callout>
    </Section>
  );
}

function SectionPerfil() {
  return (
    <Section id="perfil" icon={UserCircle2} title="Tu perfil profesional" eyebrow="Paso 11">
      <p className="text-sm text-muted-foreground">
        Tu perfil <Link to="/app/perfil" className="underline hover:text-foreground">/app/perfil</Link> es lo que ven los colegas
        cuando evalúan asignarte una delegación o mediación. Manténlo actualizado.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="panel p-5">
          <div className="font-medium">Foto de perfil</div>
          <p className="mt-2 text-xs text-muted-foreground">
            Sube una foto profesional (JPG/PNG/WEBP, máx 2 MB). Si eres Pionero o Fundador, aparecerás en el
            <Link to="/embajador-pionero" className="underline mx-1">carrusel público</Link>
            de Embajadores con tu foto destacada.
          </p>
        </div>
        <div className="panel p-5">
          <div className="font-medium">Documentos verificados</div>
          <p className="mt-2 text-xs text-muted-foreground">
            Cédula, título y certificado CS. Si no aparecen como <Pill color="emerald">Verificado</Pill> aún, contacta al equipo.
          </p>
        </div>
        <div className="panel p-5">
          <div className="font-medium">Datos profesionales</div>
          <p className="mt-2 text-xs text-muted-foreground">
            Especialidades (cambia según tu enfoque actual), región, comuna, bio. Estos campos definen tu visibilidad en búsquedas.
          </p>
        </div>
        <div className="panel p-5">
          <div className="font-medium">Métodos de cobro</div>
          <p className="mt-2 text-xs text-muted-foreground">
            Datos bancarios para recibir los pagos liberados (cuenta vista o corriente, RUT, banco). Información cifrada.
          </p>
        </div>
      </div>
    </Section>
  );
}

function SectionNotificaciones() {
  return (
    <Section id="notificaciones" icon={Bell} title="Notificaciones in-app y push" eyebrow="Paso 12">
      <p className="text-sm text-muted-foreground">
        Colega Link te avisa de eventos importantes por dos canales:
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="panel p-5">
          <Bell size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 font-medium">Campana in-app</div>
          <p className="mt-2 text-xs text-muted-foreground">
            En la esquina superior derecha de la app. Muestra contador rojo cuando hay nuevas notificaciones.
            Click para ver el panel con todas las novedades.
          </p>
        </div>
        <div className="panel p-5">
          <Smartphone size={18} className="text-[hsl(var(--accent))]" />
          <div className="mt-3 font-medium">Push notifications (WhatsApp-style)</div>
          <p className="mt-2 text-xs text-muted-foreground">
            La primera vez que entres, el navegador te pedirá permiso. <strong className="text-foreground">Acepta</strong> para
            recibir avisos aunque la app esté cerrada, con sonido como WhatsApp.
          </p>
        </div>
      </div>

      <Callout type="info" title="¿Qué eventos disparan notificaciones?">
        <ul className="space-y-1 mt-1">
          <li>• Nueva postulación a tu delegación / mediación</li>
          <li>• Contraoferta recibida</li>
          <li>• Asignación confirmada</li>
          <li>• Comprobante subido (te toca revisar y aprobar)</li>
          <li>• Pago liberado (cuando seas el delegado)</li>
          <li>• Mensaje del equipo Colega Link</li>
          <li>• Disputa abierta o resuelta</li>
        </ul>
      </Callout>
    </Section>
  );
}

function SectionPWA() {
  return (
    <Section id="pwa" icon={Smartphone} title="Instala Colega Link como app" eyebrow="Paso 13">
      <p className="text-sm text-muted-foreground">
        Colega Link es una <strong className="text-foreground">PWA (Progressive Web App)</strong>: puedes instalarla en tu
        teléfono o computador y se ve igual que una app nativa, con icono en pantalla y notificaciones push.
      </p>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="panel p-5">
          <div className="font-medium text-sm">Android</div>
          <ol className="mt-2 space-y-1 text-xs text-muted-foreground list-decimal list-inside">
            <li>Abre Chrome → colegalink.cl</li>
            <li>Menú ⋮ → "Instalar app"</li>
            <li>Confirma → ícono aparece en escritorio</li>
          </ol>
        </div>
        <div className="panel p-5">
          <div className="font-medium text-sm">iPhone</div>
          <ol className="mt-2 space-y-1 text-xs text-muted-foreground list-decimal list-inside">
            <li>Abre Safari → colegalink.cl</li>
            <li>Tap "Compartir" → "Añadir a inicio"</li>
            <li>Confirma → ícono en pantalla</li>
          </ol>
        </div>
        <div className="panel p-5">
          <div className="font-medium text-sm">Windows / Mac</div>
          <ol className="mt-2 space-y-1 text-xs text-muted-foreground list-decimal list-inside">
            <li>Chrome / Edge → colegalink.cl</li>
            <li>Icono "Instalar" en barra de URL</li>
            <li>Click → app standalone</li>
          </ol>
        </div>
      </div>
    </Section>
  );
}

function SectionReglas() {
  return (
    <Section id="reglas" icon={Shield} title="Reglas de oro" eyebrow="Paso 14 · lee con calma">
      <Callout type="danger" title="Anti-desintermediación">
        <strong>Nunca</strong> intercambies datos de contacto (teléfono, email, WhatsApp) con colegas <em>antes</em> de
        cerrar el Pago Protegido. Operar fuera de la plataforma viola los Términos y resulta en suspensión + cobro retroactivo
        de comisión.
      </Callout>

      <Callout type="warn" title="Confidencialidad de antecedentes">
        Solo el delegado asignado puede ver los archivos del caso. Está prohibido reenviarlos, fotografiarlos o
        compartirlos con terceros. Audit trail completo: sabemos quién descargó qué y cuándo.
      </Callout>

      <Callout type="warn" title="Honorarios justos">
        El sistema sugiere rangos por tipo + región. Publicar honorarios muy por debajo del mercado para "ganar"
        es legal pero te genera mala reputación. La calificación post-trabajo afecta tu ranking.
      </Callout>

      <Callout type="warn" title="Comprobantes verídicos">
        Subir comprobantes falsos (acta de audiencia inexistente, certificado retocado, etc.) es <strong>causal de baneo
        permanente</strong> + denuncia ante la Corte Suprema.
      </Callout>

      <Callout type="success" title="Buenas prácticas">
        <ul className="space-y-1 mt-1">
          <li>• Responde postulaciones en menos de 24h.</li>
          <li>• Sube los antecedentes inmediatamente después de asignar.</li>
          <li>• Aprueba el pago dentro de 48h si el comprobante es válido.</li>
          <li>• Califica honestamente al colega después de cada trabajo.</li>
        </ul>
      </Callout>
    </Section>
  );
}

function SectionSoporte() {
  return (
    <Section id="soporte" icon={Sparkles} title="Soporte y contacto" eyebrow="Paso 15 · estamos contigo">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="panel p-5">
          <div className="font-medium">Canal Pioneros (privado)</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Si eres Pionero o Pionero Fundador, recibes invitación al grupo cerrado de WhatsApp donde decidimos juntos qué
            features priorizar y avisamos cambios antes que a nadie.
          </p>
        </div>
        <div className="panel p-5">
          <div className="font-medium">Email de soporte</div>
          <p className="mt-2 text-sm text-muted-foreground">
            <a href="mailto:info.vortexchile@gmail.com" className="underline hover:text-foreground">info.vortexchile@gmail.com</a> —
            respuesta en menos de 24h hábiles.
          </p>
        </div>
        <div className="panel p-5">
          <div className="font-medium">Disputas</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Desde cualquier delegación o mediación en curso puedes abrir una <Pill color="red">Disputa</Pill>. El equipo
            mediará entre las partes y resolverá en máx 72h.
          </p>
        </div>
        <div className="panel p-5">
          <div className="font-medium">Términos y privacidad</div>
          <p className="mt-2 text-sm text-muted-foreground">
            Lee siempre los <Link to="/terminos" className="underline hover:text-foreground">Términos de uso</Link> antes de
            operar. Definen responsabilidades de cada parte.
          </p>
        </div>
      </div>

      <div className="mt-6 text-center panel p-8 bg-gradient-to-br from-[hsl(var(--accent))]/10 to-background">
        <Sparkles size={20} className="text-[hsl(var(--accent))] mx-auto" />
        <h3 className="serif text-2xl mt-3">¡Bienvenido a Colega Link!</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
          Estamos construyendo esto contigo. Cualquier sugerencia o bug que encuentres, escríbenos. Tu input directo
          es lo que hace que el producto mejore semana a semana.
        </p>
        <div className="mt-5 flex gap-2 justify-center flex-wrap">
          <Link to="/app" className="btn-primary inline-flex items-center gap-1.5">
            Ir al panel <ArrowRight size={14} />
          </Link>
          <MediatorAware fallback={
            <Link to="/embajador-pionero" className="btn-ghost text-sm inline-flex items-center gap-1.5">
              Reservar cupo Pionero <Crown size={14} />
            </Link>
          }>
            {null}
          </MediatorAware>
        </div>
      </div>
    </Section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <ChevronRight size={13} className="text-[hsl(var(--accent))] mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

// Oculta contenido para usuarios con rol mediator. Si `fallback` está definido y
// el usuario NO es mediator, renderiza el fallback; si es mediator devuelve null.
function MediatorAware({ fallback, children }) {
  const { user } = useAuth();
  if (user?.role === "mediator") return null;
  return fallback || children || null;
}
